import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OutboundCallerBrand from "../OutboundCallerBrand/OutboundCallerBrand";
import styles from "./PounceBulkUploadSuccessPage.module.css";

const getSafeReturnTo = (value) =>
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//")
    ? value
    : "/add-lead";

const getCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

const PounceBulkUploadSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state ?? {};

  const importResult = state.importResult;
  const summary = importResult?.summary ?? {};
  const imported = getCount(summary.imported);
  const invalid = getCount(summary.invalid);
  const duplicates = getCount(summary.duplicate);
  const failed = getCount(summary.failed);
  const unverified = getCount(summary.unverified);
  const returnTo = getSafeReturnTo(state.returnTo);

  useEffect(() => {
    if (!importResult || imported === 0) {
      navigate("/bulk-upload", {
        replace: true,
        state: { returnTo },
      });
    }
  }, [importResult, imported, navigate, returnTo]);

  if (!importResult || imported === 0) return null;

  return (
    <div className={styles.page}>
      <header className={styles.brand}>
        <OutboundCallerBrand />
      </header>

      <main className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.successMark} aria-hidden="true">
            <svg
              width="100"
              height="100"
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Radial Burst Rays (Pounce Purple & Emerald Green as per Figma) */}
              {/* Top Left - Purple */}
              <line
                x1="50"
                y1="12"
                x2="50"
                y2="4"
                stroke="#6524EB"
                strokeWidth="3.2"
                strokeLinecap="round"
                transform="rotate(-42 50 50)"
              />
              {/* Mid Left - Green */}
              <line
                x1="50"
                y1="12"
                x2="50"
                y2="4"
                stroke="#10B981"
                strokeWidth="3.2"
                strokeLinecap="round"
                transform="rotate(-82 50 50)"
              />
              {/* Bottom Left - Purple */}
              <line
                x1="50"
                y1="12"
                x2="50"
                y2="4"
                stroke="#6524EB"
                strokeWidth="3.2"
                strokeLinecap="round"
                transform="rotate(-128 50 50)"
              />

              {/* Top Right - Purple */}
              <line
                x1="50"
                y1="12"
                x2="50"
                y2="4"
                stroke="#6524EB"
                strokeWidth="3.2"
                strokeLinecap="round"
                transform="rotate(42 50 50)"
              />
              {/* Mid Right - Green */}
              <line
                x1="50"
                y1="12"
                x2="50"
                y2="4"
                stroke="#10B981"
                strokeWidth="3.2"
                strokeLinecap="round"
                transform="rotate(82 50 50)"
              />
              {/* Bottom Right - Purple */}
              <line
                x1="50"
                y1="12"
                x2="50"
                y2="4"
                stroke="#6524EB"
                strokeWidth="3.2"
                strokeLinecap="round"
                transform="rotate(128 50 50)"
              />

              {/* Soft Green Circle */}
              <circle cx="50" cy="50" r="30" fill="#DCFCE7" />

              {/* Rounded Green Checkmark */}
              <path
                d="M39.5 50.5L46.5 57.5L60.5 42.5"
                stroke="#16A34A"
                strokeWidth="4.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h1>Leads Imported Successfully</h1>
          <p>
            {imported} {imported === 1 ? "lead has" : "leads have"} been
            imported and will now be processed by Pounce.
          </p>
        </section>

        <section className={styles.summary} aria-label="Import summary">
          <div className={`${styles.summaryRow} ${styles.importedRow}`}>
            <span className={styles.statusIcon} aria-hidden="true">
              ✓
            </span>
            <strong>{imported}</strong>
            <span>Imported Leads</span>
            <span className={styles.resultBadge}>Successfully imported</span>
          </div>

          <div className={`${styles.summaryRow} ${styles.invalidRow}`}>
            <span className={styles.statusIcon} aria-hidden="true">
              ×
            </span>
            <strong>{invalid}</strong>
            <span>Skipped (Invalid)</span>
            <span className={styles.resultBadge}>Not imported</span>
          </div>

          <div className={`${styles.summaryRow} ${styles.duplicateRow}`}>
            <span className={styles.statusIcon} aria-hidden="true">
              ⧉
            </span>
            <strong>{duplicates}</strong>
            <span>Duplicates</span>
            <span className={styles.resultBadge}>Not imported</span>
          </div>

          {(failed > 0 || unverified > 0) && (
            <p className={styles.extraResults}>
              {failed > 0 && `${failed} failed`}
              {failed > 0 && unverified > 0 && " · "}
              {unverified > 0 && `${unverified} unverified`}
            </p>
          )}
        </section>

        <section className={styles.nextSteps}>
          <div className={styles.nextStepsHeader}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6524EB"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
              <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
              <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
              <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
            </svg>
            <h2>What happens next?</h2>
          </div>
          <ol>
            <li>
              Pounce will start calling eligible imported leads to have natural
              conversations.
            </li>
            <li>We’ll identify qualified leads based on your criteria.</li>
            <li>
              Booked appointments will be automatically added to your calendar.
            </li>
          </ol>
        </section>

        <div className={styles.actions}>
          <button
            className={styles.primaryButton}
            type="button"
            onClick={() =>
              navigate("/leads?tab=all", {
                state: { fromBulkUploadSuccess: true },
              })
            }
          >
            View Leads
          </button>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() =>
              navigate("/bulk-upload", {
                state: { returnTo },
              })
            }
          >
            Upload More Leads
          </button>
        </div>
      </main>
    </div>
  );
};

export default PounceBulkUploadSuccessPage;
